import { useRef } from "react";

import { useModal } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import useClassroomGetList from "@/classrooms/useClassroomGetList";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import ChecksForUnderstandingStudentContainer from "@/view/ChecksForUnderstandingStudentContainer";
import contentTypes from "@/view/constants/contentTypes";
import quizQueryParams from "@/view/constants/quizQueryParams";
import FormativeTOSModal from "@/view/FormativeTOSModal";
import TeacherActivityContainer from "@/view/TeacherActivityContainer";
import getBestMatchLevelForAttachments from "@/view/utils/getBestMatchLevelForAttachments";
import getGuidedHighlightingQuizAttachment from "@/view/utils/getGuidedHighlightingQuizAttachment";
import useActivityPanel from "@/view/utils/useActivityPanel";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";
import useFormativeTOS from "@/view/utils/useFormativeTOS";
import useFormativeWebTokenGet from "@/view/utils/useFormativeWebTokenGet";
import useGroupAttachments from "@/view/utils/useGroupAttachments";
import useSetAssignmentActivities from "@/view/utils/useSetAssignmentActivities";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import useExportableVisibility from "@/assignments/useExportableVisibility";

import AnnotationsContainer from "../AnnotationsContainer";
import ArticleActivityPicker from "../ArticleActivityPicker";
import AssignmentInstructionsContainer from "../AssignmentInstructionsContainer";
import GuidedHighlightingContainer from "../GuidedHighlightingContainer";
import { GUIDED_HIGHLIGHTING_ACTIVITY_PANEL } from "../GuidedHighlightingContainer/utils/utils";
import PowerWordsContainer from "../PowerWordsContainer";
import QuizContainer from "../QuizContainer";
import ReadingSkillsQuizContainer from "../ReadingSkillsQuizContainer";
import getDefaultQuizAttachment from "../utils/getDefaultQuizAttachment/getDefaultQuizAttachment";
import VideoActivityContainer from "../VideoActivityContainer";
import WritePromptContainer from "../WritePromptContainer";

const propTypes = {
  activeVideoActivityLabel: PropTypes.string,
  assignments: PropTypes.array,
  assignmentsLoading: PropTypes.bool,
  content: PropTypes.object,
  contentId: PropTypes.string,
  contentMetadata: PropTypes.arrayOf(PropTypes.object),
  contentType: PropTypes.oneOfType([PropTypes.null, PropTypes.string]),
  currentLevel: PropTypes.object,
  hideAssignments: PropTypes.bool,
  isPresentModeActive: PropTypes.bool,
  inProgressPowerWords: PropTypes.object,
  inProgressQuizzes: PropTypes.object,
  legacyArticleLevelData: PropTypes.object,
  onClickCloseButton: PropTypes.func,
  quizOmissionReason: PropTypes.oneOfType([PropTypes.null, PropTypes.string]),
  selectedAssignment: PropTypes.object,
  setActiveVideoActivityLabel: PropTypes.func,
  studentId: PropTypes.number,
  updateinProgressPowerWords: PropTypes.func,
  updateinProgressQuizzes: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  userProperties: PropTypes.object,
};

const QUIZ_OVERVIEW_PARAM = quizQueryParams.QUIZ_OVERVIEW_PARAM;
const QUIZ_PREVIEW_PARAM = quizQueryParams.QUIZ_PREVIEW_PARAM;

const ActivityContainer = ({
  activeVideoActivityLabel,
  assignments,
  assignmentsLoading,
  content,
  contentId,
  contentMetadata,
  contentType,
  currentLevel,
  hideAssignments,
  isBannerModeActive,
  isPresentModeActive,
  inProgressPowerWords,
  inProgressQuizzes,
  legacyArticleLevelData,
  quizOmissionReason,
  selectedAssignment,
  setActiveVideoActivityLabel,
  studentId,
  unlockLevel,
  updateinProgressPowerWords,
  updateinProgressQuizzes,
  userId,
  userProperties,
  fetchAssignment,
  isSelectedAssignmentLoading,
  latestAssignment,
}) => {
  const { setIsActivityPanelExpanded } = useActivityPanel();
  const { isStudent, isTeacher, activeELALicense } = userProperties;
  const router = useRouter();
  const {
    query: {
      activityPanel,
      assignmentId,
      levelId,
      quizId,
      writePromptId,
      powerWordsActivityId,
      selectedAssignmentId,
    },
  } = router;
  const {
    attachments,
    error: attachmentsError,
    setCurrentActivityCount,
    setVisibleActivityIds,
    visibleActivityIds,
  } = useAttachmentsContext();
  const [formativeModalProps, setFormativeModalActive, triggerRef] =
    useModal(false);
  const setFormativeModalOpen = () => setFormativeModalActive(true);
  const setFormativeModalClosed = () => setFormativeModalActive(false);

  const canSeeExportables = useExportableVisibility({
    articleType: content?.data?.article_type,
    contentMetadata,
    contentType,
    currentLevel,
  });

  const { data: classroomList, loading: classroomListLoading } =
    useClassroomGetList();
  const shouldFocusCloseButtonRef = useRef(false);

  const activityToLevelMapping =
    attachments && getBestMatchLevelForAttachments(attachments, currentLevel);
  const selectedPowerWordsLevel =
    powerWordsActivityId && attachments
      ? activityToLevelMapping[powerWordsActivityId]
      : null;
  const selectedQuizLevel =
    quizId && attachments ? activityToLevelMapping[quizId] : null;
  const selectedWritePromptLevel =
    writePromptId && attachments ? activityToLevelMapping[writePromptId] : null;

  const { isStudentPreviewOn } = useStudentPreviewContext();
  const defaultQuizAttachment = getDefaultQuizAttachment(attachments);
  const defaultQuizLevel = defaultQuizAttachment
    ? activityToLevelMapping[defaultQuizAttachment?.id]
    : null;

  const [useOAuthFormativeExperiment] = useGetUserExperimentSegment(
    "use-oauth-formative",
  );
  const canSeeFormative = userProperties?.canSeeFormative;
  const { data: formativeJwtResponse, loading: jwtReponseLoading } =
    useFormativeWebTokenGet(
      {
        contentId,
        quizId: defaultQuizLevel?.id,
        rawLexile: currentLevel?.raw_lexile,
        useOAuth: useOAuthFormativeExperiment === "on" ? "yes" : undefined,
      },
      !isTeacher,
    );
  const {
    currentTOSVersion,
    formativeTOSText,
    shouldDisplayFormativeTOS,
    setShouldDisplayFormativeTOS,
  } = useFormativeTOS({ userId });

  const shouldDisplayFormative =
    isTeacher &&
    !isStudentPreviewOn &&
    !jwtReponseLoading &&
    !!formativeJwtResponse &&
    canSeeFormative;

  const { attachmentsGroupedBySubject, filteredAttachmentsByLanguage } =
    useGroupAttachments({
      activityToLevelMapping,
      attachments,
      currentLevel,
      isTeacher,
      selectedAssignment,
      setCurrentActivityCount,
    });

  const isSavedAssignment = getIsSavedAssignment(selectedAssignmentId);
  const { updatedAssignment, dispatch } = useAssignmentReducerContext();

  // select/check all activities for unsaved assignments
  useSetAssignmentActivities({
    assignmentModifiedDate: updatedAssignment?.date_modified,
    attachmentsByLanguage: filteredAttachmentsByLanguage,
    dispatch,
    hasUnsavedChanges: updatedAssignment?.hasUnsavedChanges ?? false,
    isSavedAssignment,
    userProperties,
  });

  const onClickCloseButton = () => {
    setIsActivityPanelExpanded(false);
  };

  const closeButtonEventProperties = {
    objectType: "Content",
    actionName: "Hide",
    streamProperties: {
      content_type: "Article",
      description: "Close Activity Panel",
      content_id: currentLevel?.id,
    },
  };

  const renderChecksForUnderstandingContainer = () => {
    if (activityPanel !== "checks_for_understanding") return null;

    return (
      <ChecksForUnderstandingStudentContainer
        closeButtonEventProperties={closeButtonEventProperties}
        onClickCloseButton={onClickCloseButton}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      />
    );
  };

  const renderPowerWordsContainer = () => {
    if (
      !powerWordsActivityId ||
      activityPanel !== "power_words_activity" ||
      !selectedPowerWordsLevel
    )
      return null;
    return (
      <PowerWordsContainer
        closeButtonEventProperties={closeButtonEventProperties}
        data={selectedPowerWordsLevel}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        inProgressPowerWords={inProgressPowerWords}
        isStudent={isStudent}
        legacyArticleLevelData={legacyArticleLevelData}
        onClickCloseButton={onClickCloseButton}
        studentId={studentId}
        updateinProgressPowerWords={updateinProgressPowerWords}
        userProperties={userProperties}
        isPresentModeActive={isPresentModeActive}
        contentId={contentId}
      />
    );
  };

  const renderQuizContainer = () => {
    if (!quizId || activityPanel !== "quiz") {
      return null;
    }

    return (
      <QuizContainer
        closeButtonEventProperties={closeButtonEventProperties}
        currentAssignment={selectedAssignment}
        currentLevelRawLexile={currentLevel?.raw_lexile}
        currentLevelId={currentLevel?.id}
        language={currentLevel?.language}
        data={selectedQuizLevel}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        inProgressQuizzes={inProgressQuizzes}
        isBannerModeActive={isBannerModeActive}
        onClickCloseButton={onClickCloseButton}
        unlockLevel={unlockLevel}
        updateinProgressQuizzes={updateinProgressQuizzes}
        userProperties={userProperties}
        assignments={assignments}
        contentId={contentId}
      />
    );
  };

  const renderReadingSkillsQuizContainer = () => {
    if (
      activityPanel !== QUIZ_OVERVIEW_PARAM &&
      activityPanel !== QUIZ_PREVIEW_PARAM
    ) {
      return null;
    }

    return (
      <ReadingSkillsQuizContainer
        closeButtonEventProperties={closeButtonEventProperties}
        data={selectedQuizLevel}
        isPresentModeActive={isPresentModeActive}
        onClickCloseButton={onClickCloseButton}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        language={currentLevel?.language}
      />
    );
  };

  const renderAnnotationContainer = () => {
    if (activityPanel !== "annotations") {
      return null;
    }

    return (
      <AnnotationsContainer
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        legacyArticleId={legacyArticleLevelData?.id}
        onClickCloseButton={onClickCloseButton}
        userId={userId}
        userProperties={userProperties}
      />
    );
  };

  const renderGuidedHighlightingContainer = () => {
    if (activityPanel !== GUIDED_HIGHLIGHTING_ACTIVITY_PANEL) {
      return null;
    }

    const guidedHighlightingContentId = attachments?.find(
      (attachment) => attachment.content_type === "GUIDED_HIGHLIGHTING_QUIZ",
    )?.id;

    const guidedHighlightingQuizAttachment =
      getGuidedHighlightingQuizAttachment(attachments);

    const guidedHighlightingData =
      guidedHighlightingQuizAttachment?.data?.levels?.find(
        (level) => level.article_level_id === levelId,
      );
    return (
      <GuidedHighlightingContainer
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        legacyArticleId={legacyArticleLevelData?.id}
        onClickCloseButton={onClickCloseButton}
        userId={userId}
        userProperties={userProperties}
        contentId={contentId}
        level={guidedHighlightingData}
        guidedHighlightId={guidedHighlightingContentId}
      />
    );
  };

  const renderWritePromptContainer = () => {
    if (!writePromptId || activityPanel !== "write_prompt") {
      return null;
    }

    return (
      <WritePromptContainer
        classroomListLoading={classroomListLoading}
        classrooms={classroomList}
        closeButtonEventProperties={closeButtonEventProperties}
        currentArticleLevel={currentLevel}
        data={selectedWritePromptLevel}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        legacyArticleLevelHeaderId={legacyArticleLevelData?.header_id}
        legacyArticleLevelId={legacyArticleLevelData?.id}
        onClickCloseButton={onClickCloseButton}
        studentId={studentId}
        userProperties={userProperties}
        assignments={assignments}
      />
    );
  };

  const renderAssignmentInstructionsContainer = () => {
    if (!isStudent || !assignmentId || activityPanel !== "assignment") {
      return null;
    }

    return (
      <AssignmentInstructionsContainer
        assignments={assignments}
        assignmentsLoading={assignmentsLoading}
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        onClickCloseButton={onClickCloseButton}
      />
    );
  };

  const videoActivityContainer = (
    <VideoActivityContainer
      closeButtonEventProperties={closeButtonEventProperties}
      activeVideoActivityLabel={activeVideoActivityLabel}
      setActiveVideoActivityLabel={setActiveVideoActivityLabel}
      onClickCloseButton={onClickCloseButton}
      shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
    />
  );

  const articleActivityPicker = (
    <>
      {isTeacher && !isStudentPreviewOn ? (
        <TeacherActivityContainer
          activeELALicense={activeELALicense}
          assignments={assignments}
          assignmentsLoading={assignmentsLoading}
          attachments={attachments}
          attachmentsGroupedBySubject={attachmentsGroupedBySubject}
          canSeeExportables={canSeeExportables}
          closeButtonEventProperties={closeButtonEventProperties}
          content={content}
          contentId={contentId}
          currentLevel={currentLevel}
          dispatch={dispatch}
          fetchAssignment={fetchAssignment}
          formativeJwtResponse={formativeJwtResponse}
          isSelectedAssignmentLoading={isSelectedAssignmentLoading}
          latestAssignment={latestAssignment}
          legacyArticleLevelHeaderId={legacyArticleLevelData?.header_id}
          modalTriggerRef={triggerRef}
          onClickCloseButton={onClickCloseButton}
          onModalOpen={setFormativeModalOpen}
          quizOmissionReason={quizOmissionReason}
          router={router}
          selectedAssignment={selectedAssignment}
          setVisibleActivityIds={setVisibleActivityIds}
          shouldDisplayFormative={shouldDisplayFormative}
          shouldDisplayFormativeTOS={shouldDisplayFormativeTOS}
          shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
          updatedAssignment={updatedAssignment}
          userProperties={userProperties}
          visibleActivityIds={visibleActivityIds}
        />
      ) : (
        <ArticleActivityPicker
          assignments={assignments}
          assignmentsLoading={assignmentsLoading}
          attachments={attachments}
          attachmentsError={attachmentsError}
          closeButtonEventProperties={closeButtonEventProperties}
          contentId={contentId}
          contentType={contentType}
          currentLevel={currentLevel}
          hideAssignments={hideAssignments}
          isBannerModeActive={isBannerModeActive}
          language={currentLevel?.language}
          legacyArticleLevelHeaderId={legacyArticleLevelData?.header_id}
          onClickCloseButton={onClickCloseButton}
          quizOmissionReason={quizOmissionReason}
          shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
          userProperties={userProperties}
        />
      )}
      {shouldDisplayFormative && (
        <FormativeTOSModal
          {...formativeModalProps}
          currentTOSVersion={currentTOSVersion}
          data={formativeJwtResponse}
          formativeTOSText={formativeTOSText}
          onClose={setFormativeModalClosed}
          setShouldDisplayFormativeTOS={setShouldDisplayFormativeTOS}
          userId={userId}
        />
      )}
    </>
  );

  const renderActivityPickerComponent = () => {
    if (contentType === contentTypes.INTERACTIVE_VIDEO) {
      return videoActivityContainer;
    }

    return articleActivityPicker;
  };

  const renderPanelContents = () => {
    const shouldShowActivityPicker = !activityPanel?.length;
    if (shouldShowActivityPicker) {
      return renderActivityPickerComponent();
    } else {
      return (
        <>
          {renderChecksForUnderstandingContainer()}
          {renderQuizContainer()}
          {renderReadingSkillsQuizContainer()}
          {renderWritePromptContainer()}
          {renderAssignmentInstructionsContainer()}
          {renderAnnotationContainer()}
          {renderGuidedHighlightingContainer()}
          {renderPowerWordsContainer()}
        </>
      );
    }
  };

  return renderPanelContents();
};

ActivityContainer.propTypes = propTypes;
export default ActivityContainer;
